import React, { useState, useMemo } from 'react';
import TeachingTooltip from '@/components/TeachingTooltip';
import { InlineMath, BlockMath } from 'react-katex';
import { useTheme } from '@/context/ThemeContext';

const ImpedanceReferralCalc = () => {
  const { dark } = useTheme();
  const [v1, setV1]       = useState(138);    // primary kV LL
  const [v2, setV2]       = useState(13.8);   // secondary kV LL
  const [zOrig, setZOrig] = useState(0.5);    // Ω — impedance to be referred
  const [dir, setDir]     = useState('sec_to_pri'); // 'sec_to_pri' | 'pri_to_sec'

  const calc = useMemo(() => {
    const a  = v2 > 0 ? v1 / v2 : 1;
    const a2 = a * a;
    const zRef = dir === 'sec_to_pri' ? zOrig * a2 : zOrig / a2;
    return { a, a2, zRef };
  }, [v1, v2, zOrig, dir]);

  const isSec2Pri = dir === 'sec_to_pri';
  const formulaTex = isSec2Pri
    ? `Z_{\\text{pri}} = Z_{\\text{sec}} \\times a^2 = ${zOrig} \\times (${calc.a.toFixed(4)})^2 = ${zOrig} \\times ${calc.a2.toFixed(4)} = ${calc.zRef.toFixed(4)}\\,\\Omega`
    : `Z_{\\text{sec}} = Z_{\\text{pri}} \\times \\frac{1}{a^2} = ${zOrig} \\times \\frac{1}{(${calc.a.toFixed(4)})^2} = \\frac{${zOrig}}{${calc.a2.toFixed(4)}} = ${calc.zRef.toFixed(6)}\\,\\Omega`;

  const barData = [
    { name: isSec2Pri ? 'Z_sec (original)' : 'Z_pri (original)', val: parseFloat(zOrig.toFixed(6)) },
    { name: isSec2Pri ? 'Z_pri (referred)' : 'Z_sec (referred)', val: parseFloat(calc.zRef.toFixed(6)) },
  ];

  // Simple bar SVG to avoid recharts complexity with very small/large values
  const maxVal = Math.max(barData[0].val, barData[1].val, 1e-10);
  const barColors = ['#6366f1', '#0d9488'];

  return (
    <div data-testid="impedance-referral-calc" className="space-y-5">

      {/* Direction selector */}
      <div>
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Referral direction</p>
        <div className="flex flex-wrap gap-3">
          {[
            { key: 'sec_to_pri', label: 'Secondary → Primary (× a²)' },
            { key: 'pri_to_sec', label: 'Primary → Secondary (÷ a²)' },
          ].map(({ key, label }) => (
            <button
              key={key}
              data-testid={`direction-btn-${key}`}
              onClick={() => setDir(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                dir === key
                  ? 'bg-teal-600 text-white border-teal-600'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-teal-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-500 mt-1.5">
          {isSec2Pri
            ? 'Moving from LV side to HV side: impedance multiplies by a² (gets LARGER).'
            : 'Moving from HV side to LV side: impedance divides by a² (gets SMALLER).'}
        </p>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">

        {/* V1 */}
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">V₁ — Primary V<sub>LL</sub> (kV)</label>
            <TeachingTooltip
              showIcon
              whatThis="The rated HV-side line-to-line voltage. Used only to compute the turns ratio a = V1/V2."
              tryThis="Use 138 kV / 13.8 kV for a typical distribution transformer (a = 10, a² = 100)."
              whyMatters="The turns ratio completely determines the impedance scaling. Doubling V1 quadruples the referral factor a²."
              connectedTo="→ Computes a = V1/V2 and a² displayed in the result cards."
            />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="v1-refer-input" type="range" min="1" max="765" step="0.1" value={v1}
              onChange={e => setV1(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={v1} step="0.1" min="1"
              onChange={e => setV1(Math.max(0.1, Number(e.target.value)))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none" />
            <span className="text-xs text-slate-500 w-7">kV</span>
          </div>
        </div>

        {/* V2 */}
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">V₂ — Secondary V<sub>LL</sub> (kV)</label>
            <TeachingTooltip
              showIcon
              whatThis="The rated LV-side line-to-line voltage. Used to compute a = V1/V2."
              tryThis="Change from 13.8 kV to 4.16 kV — watch a² jump from 100 to 1102. A 1Ω secondary fault referred to 138 kV becomes over 1 kΩ!"
              whyMatters="Errors in V2 cause squared errors in the referred impedance. Always use rated voltage, not actual operating voltage."
              connectedTo="→ Affects a² and therefore Z_referred directly by the square of the ratio change."
            />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="v2-refer-input" type="range" min="0.1" max="345" step="0.1" value={v2}
              onChange={e => setV2(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={v2} step="0.1" min="0.1"
              onChange={e => setV2(Math.max(0.1, Number(e.target.value)))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none" />
            <span className="text-xs text-slate-500 w-7">kV</span>
          </div>
        </div>

        {/* Z_original */}
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Z — {isSec2Pri ? 'Secondary' : 'Primary'} Impedance (Ω)
            </label>
            <TeachingTooltip
              showIcon
              whatThis={`The impedance on the ${isSec2Pri ? 'secondary (LV)' : 'primary (HV)'} side that you want to refer to the ${isSec2Pri ? 'primary (HV)' : 'secondary (LV)'} side.`}
              tryThis="Enter 0.5Ω as a secondary fault impedance on a 138/13.8 kV transformer. Watch it become 50Ω on the primary — 100× larger because a² = 100."
              whyMatters="In fault analysis using a single-voltage equivalent circuit, ALL impedances must be referred to the same side. The a² factor is the entire reason per-unit analysis was invented."
              connectedTo="→ Multiplied (or divided) by a² to give Z_referred on the other side."
            />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="z-orig-input" type="range" min="0.001" max="500" step="0.001" value={zOrig}
              onChange={e => setZOrig(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={zOrig} step="0.001" min="0.001"
              onChange={e => setZOrig(Math.max(0.001, Number(e.target.value)))}
              className="w-24 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none" />
            <span className="text-xs text-slate-500 w-4">Ω</span>
          </div>
        </div>
      </div>

      {/* Result cards */}
      <div className="grid grid-cols-3 gap-3">
        <div data-testid="turns-ratio-refer-result" className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">Turns Ratio a</p>
          <p className="text-xl font-bold font-mono text-slate-900 dark:text-white">{calc.a.toFixed(4)}</p>
          <p className="text-[10px] text-slate-500">V₁ / V₂</p>
        </div>
        <div data-testid="a2-result" className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-purple-700 dark:text-purple-400 mb-1">a² (scaling factor)</p>
          <p className="text-xl font-bold font-mono text-purple-900 dark:text-purple-100">{calc.a2.toFixed(4)}</p>
          <p className="text-[10px] text-purple-500">Impedance multiplier</p>
        </div>
        <div data-testid="z-referred-result" className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-700 dark:text-teal-400 mb-1">Z<sub>referred</sub></p>
          <p className="text-xl font-bold font-mono text-teal-900 dark:text-teal-100">{calc.zRef.toFixed(4)}</p>
          <p className="text-[10px] text-teal-500">Ω ({isSec2Pri ? 'primary' : 'secondary'} side)</p>
        </div>
      </div>

      {/* Formula with substitution */}
      <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">Formula with full substitution</p>
        <div className="overflow-x-auto">
          <BlockMath math={formulaTex} />
        </div>
        <div className="mt-3 border-t border-slate-200 dark:border-slate-700 pt-3">
          <p className="text-xs text-red-600 dark:text-red-400 font-semibold">
            Critical: the scaling factor is <InlineMath math="a^2" />, NOT <InlineMath math="a" />.
            Forgetting the square gives an answer {calc.a.toFixed(2)}× too
            {isSec2Pri ? ' small (underestimates primary fault impedance).' : ' large (overestimates secondary fault impedance).'}
          </p>
        </div>
      </div>

      {/* Visual bar comparison */}
      <div>
        <p className="text-xs font-medium text-slate-500 dark:text-slate-500 mb-2">Impedance magnitude comparison (Ω)</p>
        <div className="space-y-3">
          {barData.map((d, i) => (
            <div key={d.name} className="flex items-center gap-3">
              <span className="text-xs font-mono w-32 text-right text-slate-600 dark:text-slate-400 flex-shrink-0">{d.name}</span>
              <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-6 overflow-hidden">
                <div
                  className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-300"
                  style={{ width: `${Math.max(4, (d.val / maxVal) * 100)}%`, backgroundColor: barColors[i] }}
                >
                  <span className="text-[10px] font-mono font-bold text-white truncate">{d.val.toFixed(4)} Ω</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-center text-slate-400 dark:text-slate-600 mt-2">
          Ratio Z_referred / Z_original = {isSec2Pri ? `a² = ${calc.a2.toFixed(4)}` : `1/a² = ${(1 / calc.a2).toFixed(6)}`}
        </p>
      </div>
    </div>
  );
};

export default ImpedanceReferralCalc;
